import axios from 'axios';
import API_URL from '../api';

/* ==================================================================
   Applications — data access
   ------------------------------------------------------------------
   Everything that talks to /api/applications lives here, so the
   student dashboard and the public job page apply through the same
   code path and read the same response shape. They used to disagree:
   the dashboard posted an application, the job page navigated the
   candidate to the dashboard and posted nothing.
   ================================================================== */

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// One palette for application status, used by every surface that shows one.
export const APPLICATION_STATUS_STYLE = {
  Applied:               { background: '#EFF6FF', color: '#1D4ED8' },
  Viewed:                { background: '#F5F3FF', color: '#6D28D9' },
  Shortlisted:           { background: '#ECFDF5', color: '#065F46' },
  'Interview Scheduled': { background: '#FFF7ED', color: '#C2410C' },
  Selected:              { background: '#F0FDF4', color: '#15803D' },
  Rejected:              { background: '#FEF2F2', color: '#B91C1C' },
};

/**
 * The reference the candidate quotes, e.g. "HYR-60718293".
 *
 * The server sends it as `application_ref` on every application. The fallback
 * derives the same string from `_id` using the same rule (see
 * backend/src/models/Application.js) so a response cached from before the field
 * existed still shows an id rather than a blank line.
 */
export function applicationRef(app) {
  if (!app) return null;
  if (app.application_ref) return app.application_ref;
  const hex = String(app._id || '');
  if (!/^[0-9a-fA-F]{24}$/.test(hex)) return null;
  return `HYR-${hex.slice(-8).toUpperCase()}`;
}

/** The job id behind an application, whether or not the job was populated. */
export function jobIdOf(app) {
  const value = app?.job_id;
  const id = value && typeof value === 'object' ? value._id : value;
  return id ? String(id) : null;
}

/** The job document on an application, or null when it was not populated. */
export function jobOf(app) {
  return app?.job_id && typeof app.job_id === 'object' ? app.job_id : null;
}

/** The display name of the company behind a job. */
export function companyOf(job) {
  return job?.posted_by?.company_name || job?.posted_by?.name || job?.company_name || 'Company';
}

/**
 * Every application this candidate has submitted, newest first.
 * `jobId` narrows to one job — what a job page asks to find out whether the
 * candidate has already applied to the job in front of them.
 */
export async function fetchApplications(token, { jobId } = {}) {
  const query = jobId ? `?job_id=${encodeURIComponent(jobId)}` : '';
  const { data } = await axios.get(`${API_URL}/api/applications${query}`, auth(token));
  return Array.isArray(data) ? data : [];
}

/**
 * Apply to a job.
 *
 * Resolves either way rather than throwing, because the interesting outcomes
 * are not exceptions: an unverified profile, an unreachable resume and an
 * application that already exists are all states the UI renders, each with its
 * own next step. Only the transport is allowed to be a surprise, and it lands
 * here as code 'APPLY_FAILED'.
 *
 * @returns {Promise<{ok: boolean, application: object|null, code?: string,
 *                     message?: string, screeningStatus?: string|null}>}
 */
export async function submitApplication(token, jobId) {
  try {
    const { data } = await axios.post(`${API_URL}/api/applications`, { job_id: jobId }, auth(token));
    return { ok: true, application: data };
  } catch (err) {
    const data = err.response?.data || {};
    return {
      ok: false,
      // ALREADY_APPLIED carries the application that already exists, so the
      // caller can show its reference instead of an error with nothing behind it.
      application: data.application || null,
      code: data.code || 'APPLY_FAILED',
      message: data.message || data.error || 'Something went wrong. Please try again.',
      screeningStatus: data.screening_status || null,
    };
  }
}
